import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Mail, MessageCircle, ChevronDown, LifeBuoy } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/suporte")({
  head: () => ({
    meta: [
      { title: "Suporte & FAQ · Beit Tefilah Rechovot" },
      {
        name: "description",
        content:
          "Perguntas frequentes e canais de suporte da área de membros da Beit Tefilah Rechovot.",
      },
    ],
  }),
  component: SuportePage,
});

const FAQ: { q: string; a: string }[] = [
  {
    q: "Meu cadastro está pendente. E agora?",
    a: "Todos os novos cadastros passam por aprovação manual de um administrador para garantir a segurança da comunidade. Você receberá acesso automaticamente assim que sua conta for liberada — normalmente em até 48 horas.",
  },
  {
    q: "Como assisto aos ensinos?",
    a: "Vá em 'Ensinos' no menu, escolha um estudo e o vídeo será reproduzido dentro da própria plataforma, integrado com o YouTube oficial da congregação. Você pode pausar e retomar depois — a plataforma lembra de onde parou.",
  },
  {
    q: "Como filtro por Parashá, data ou festividade?",
    a: "Na página 'Ensinos' há filtros por turno (manhã/tarde), ano hebraico, festividade e Parashá. Também é possível navegar por 'Parashiot' para ver todos os estudos organizados por porção da Torá.",
  },
  {
    q: "Como salvo um ensino nos favoritos?",
    a: "Toque no ícone de coração no canto de cada ensino. Todos os seus favoritos ficam disponíveis em 'Favoritos' e na página inicial.",
  },
  {
    q: "Como recupero minha senha ou altero meu e-mail?",
    a: "Sua conta é gerenciada na Gestão de cadastros da congregação (cadastro.rechovot.org.br). A redefinição de senha e a troca de e-mail são feitas lá; esta plataforma não envia e-mails de conta.",
  },

  {
    q: "Como altero meu nome, foto ou tema (claro/escuro)?",
    a: "Toque no seu avatar no canto superior direito e vá em 'Meu perfil'. Você pode alterar seus dados e escolher entre tema claro, escuro ou automático.",
  },
  {
    q: "Novos vídeos aparecem automaticamente?",
    a: "Sim. Vídeos publicados aos sábados no canal oficial da Beit Tefilah Rechovot são sincronizados automaticamente pela equipe administrativa e passam a ficar disponíveis aqui.",
  },
  {
    q: "Meus dados estão seguros?",
    a: "Sim. Utilizamos autenticação segura, senhas verificadas contra vazamentos públicos, criptografia em trânsito e políticas de acesso que garantem que apenas você veja seus dados privados (favoritos, progresso, perfil).",
  },
];

function SuportePage() {
  return (
    <div className="mx-auto max-w-3xl space-y-10">
      <header className="text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold/15">
          <LifeBuoy className="h-7 w-7 text-gold-foreground" />
        </div>
        <h1 className="mt-4 font-serif text-3xl font-semibold text-primary sm:text-4xl">
          Suporte & Perguntas Frequentes
        </h1>
        <p className="mt-3 text-muted-foreground">
          Encontre respostas rápidas ou fale com a equipe da Beit Tefilah Rechovot.
        </p>
      </header>

      <section className="grid gap-3 sm:grid-cols-2">
        <a
          href="mailto:contato@rechovot.org.br"
          className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40"
        >
          <Mail className="mt-0.5 h-5 w-5 text-primary" />
          <div>
            <p className="font-medium text-foreground">E-mail</p>
            <p className="text-sm text-muted-foreground">contato@rechovot.org.br</p>
          </div>
        </a>
        <a
          href="https://www.rechovot.org.br"
          target="_blank"
          rel="noreferrer"
          className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40"
        >
          <MessageCircle className="mt-0.5 h-5 w-5 text-primary" />
          <div>
            <p className="font-medium text-foreground">Site institucional</p>
            <p className="text-sm text-muted-foreground">rechovot.org.br</p>
          </div>
        </a>
      </section>

      <section>
        <h2 className="font-serif text-xl font-semibold text-primary sm:text-2xl">
          Perguntas frequentes
        </h2>
        <div className="mt-4 space-y-2">
          {FAQ.map((item, i) => (
            <FaqItem key={i} q={item.q} a={item.a} />
          ))}
        </div>
      </section>
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
        aria-expanded={open}
      >
        <span className="font-medium text-foreground">{q}</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && <div className="px-4 pb-4 text-sm leading-relaxed text-muted-foreground">{a}</div>}
    </div>
  );
}
